import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../../../../utils/format';
import { isKioskSessionActive } from '../../../../../../domains/flexOffice/kiosk/kioskSession.js';
import { triggerHaptic } from '../../../../../../utils/haptics.js';
import Spinner from './Spinner';

const PaymentSummaryCard = ({
    fmt,
    formatMoney,
    subtotal,
    discountAmt,
    finalAmt,
    hasDiscount,
    tax,
    ship,
    pph,
    dur,
    isGroupedOrder,
    groupedTotal,
    sessionCount,
    groupedHours,
    shoppingCartData,
    cartTotalPrice,
    isFetchingDiscounts,
    eligibleDiscounts,
    appliedDiscountIds,
    appliedDiscountsList,
    isApplyingDiscount,
    remainingTime,
    loading,
    isProcessingPayment,
    paymentMethod,
    paymentOptions = [],
    onPaymentMethodChange,
    onOpenOffersModal,
    onRemoveAllDiscounts,
    onCheckout,
    onOpenZelleModal,
    isGuestUser,
    hideCashPaymentOption = false,
}) => {
    const companyState = useSelector((state) => state?.companyReducer?.company);
    // Kiosk guests pay on a shared touchscreen device, not their own phone/laptop
    // — enlarge the tappable payment controls below rather than the compact
    // desktop/mobile-web sizing.
    const isKiosk = isKioskSessionActive();
    const money = formatMoney || ((value) => formatCurrency(value, companyState));
    const showBookingCharges = isGroupedOrder ? groupedTotal > 0 : pph > 0;
    const availableOfferCount = eligibleDiscounts?.length || 0;
    const appliedOfferCount = appliedDiscountIds?.size || 0;
    const canManageOffers = availableOfferCount > 0 || appliedOfferCount > 0;
    const renderPaymentIcon = (type, isSelected = false, variant = 'option') => {
        const iconCls = `h-5 w-5 ${variant === 'button'
            ? 'text-white'
            : isSelected
                ? 'text-blue-700'
                : 'text-slate-600'
            }`;

        if (type === 'cash') {
            return (
                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 9V7a5 5 0 00-10 0v2m-2 3h14m-13 0l.86 6.02A2 2 0 008.84 20h6.32a2 2 0 001.98-1.98L18 12m-13 0h14" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14v3m0 0c-1.105 0-2-.672-2-1.5S10.895 14 12 14s2-.672 2-1.5S13.105 11 12 11s-2 .672-2 1.5" />
                </svg>
            );
        }

        if (type === 'zelle' || type === 'upi' || type === 'qr_code' || type === 'payment_app' || type === 'bank_transfer') {
            if (variant === 'button') {
                return (
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20">
                        <span className="text-[10px] font-extrabold tracking-[0.12em] text-white">Z</span>
                    </div>
                );
            }

            return (
                <div className={`flex h-6 w-6 items-center justify-center rounded-md shadow-sm ${isSelected ? 'bg-gradient-to-br from-violet-600 to-fuchsia-500' : 'bg-gradient-to-br from-violet-500 to-fuchsia-400'}`}>
                    <span className="text-[10px] font-extrabold tracking-[0.12em] text-white">Z</span>
                </div>
            );
        }

        return (
            <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
        );
    };

    const fallbackPaymentOptions = [
        { value: 'card', label: 'Card Payment', description: 'Pay securely online using your card.' },
        { value: 'cash', label: 'Pay with Cash', description: 'Reserve now and pay in person at the venue.' },
        { value: 'zelle', label: 'Pay via Zelle', requiresReference: true },
    ];
    const basePaymentOptions = paymentOptions.length > 0 || hideCashPaymentOption
        ? paymentOptions
        : fallbackPaymentOptions;
    const availablePaymentOptions = basePaymentOptions
        .filter((option) => (
            !hideCashPaymentOption
            || (option.value !== 'cash' && option.category !== 'offline_cash')
        ));
    const selectedPaymentOption = availablePaymentOptions.find((option) => option.value === paymentMethod) || availablePaymentOptions[0] || null;

    const groupedHoursLabel = groupedHours > 0
        ? `${fmt(groupedHours)} total hour${groupedHours === 1 ? '' : 's'}`
        : '';
    const perSessionHoursLabel = dur > 0
        ? `${fmt(dur)} hour${dur === 1 ? '' : 's'} each`
        : '';
    const groupedBookingDescription = [
        `${sessionCount} session${sessionCount !== 1 ? 's' : ''}`,
        perSessionHoursLabel,
        groupedHoursLabel
    ].filter(Boolean).join(' • ');

    const ctaLabel = selectedPaymentOption?.value === 'cash'
        ? 'Confirm Cash Booking'
        : selectedPaymentOption?.requiresReference
            ? `Open ${selectedPaymentOption?.title || selectedPaymentOption?.label || 'QR Code'}`
            : selectedPaymentOption?.title || selectedPaymentOption?.label || 'Pay using Card';

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-5 py-4">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-white font-bold text-sm">Payment Summary</p>
                        <p className="text-white/70 text-xs">Breakdown of your charges</p>
                    </div>
                </div>
            </div>

            <div className="p-5 space-y-3 fx-m-card">
                {/* {summaryProductImage && (
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                        <div className="relative">
                            <img
                                src={summaryProductImage}
                                alt={summaryProductName}
                                className="h-32 w-full object-cover"
                                onError={(event) => { event.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 px-4 py-3">
                                <p className="text-sm font-bold text-white">{summaryProductName}</p>
                                {summaryProductCategory && (
                                    <p className="mt-0.5 text-xs text-white/80">{summaryProductCategory}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )} */}

                {showBookingCharges && (
                    <div className="flex justify-between items-center py-2.5 px-3.5 bg-blue-50/50 rounded-xl border border-blue-100">
                        <div>
                            <p className="text-slate-700 text-sm font-semibold">Booking Charges</p>
                            {isGroupedOrder ? (
                                <p className="text-slate-400 text-xs mt-0.5">
                                    {money(pph)}/hr • {groupedBookingDescription}
                                </p>
                            ) : (
                                <p className="text-slate-400 text-xs mt-0.5">{money(pph)} / hr - {dur} hrs</p>
                            )}
                        </div>
                        <span className="font-bold text-slate-800 text-sm">
                            {isGroupedOrder ? money(groupedTotal) : money(pph * dur)}
                        </span>
                    </div>
                )}

                <div className="flex justify-between items-center py-2.5 px-3.5 bg-slate-50 rounded-xl border border-slate-100">
                    <div>
                        <p className="text-slate-700 text-sm font-semibold">Taxes</p>
                        <p className="text-slate-400 text-xs mt-0.5">Applicable taxes</p>
                    </div>
                    <span className="font-bold text-slate-800 text-sm">{money(tax)}</span>
                </div>

                {ship > 0 && (
                    <div className="flex justify-between items-center py-2.5 px-3.5 bg-slate-50 rounded-xl border border-slate-100">
                        <p className="text-slate-700 text-sm font-semibold">Shipping & Handling</p>
                        <span className="font-bold text-slate-800 text-sm">{money(ship)}</span>
                    </div>
                )}

                {shoppingCartData && cartTotalPrice > 0 && (
                    <div className="flex justify-between items-center py-2.5 px-3.5 bg-slate-50 rounded-xl border border-slate-100">
                        <p className="text-slate-600 text-sm font-medium">Cart Total</p>
                        <span className="font-semibold text-slate-700 text-sm">{money(cartTotalPrice)}</span>
                    </div>
                )}

                <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-slate-800 text-sm font-bold">Offers & Discounts</p>
                            <p className="text-slate-500 text-xs mt-1">
                                {isFetchingDiscounts
                                    ? 'Checking active discounts for this booking.'
                                    : availableOfferCount > 0
                                        ? `${availableOfferCount} active offer${availableOfferCount !== 1 ? 's' : ''} available${appliedOfferCount > 0 ? `, ${appliedOfferCount} selected` : ''}.`
                                        : appliedOfferCount > 0
                                            ? `${appliedOfferCount} offer${appliedOfferCount !== 1 ? 's' : ''} already applied to this booking.`
                                            : 'No active discounts available for this booking right now.'}
                            </p>
                        </div>
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                            {isFetchingDiscounts ? (
                                <Spinner size={4} />
                            ) : (
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            )}
                        </div>
                    </div>

                    {appliedDiscountsList.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {appliedDiscountsList.map((discount) => (
                                <span
                                    key={discount.primaryId}
                                    className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-semibold text-blue-700"
                                >
                                    {discount.code ? <span className="font-mono">{discount.code}</span> : discount.name}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-4 flex gap-2">
                        <button
                            type="button"
                            onClick={onOpenOffersModal}
                            disabled={!canManageOffers || isFetchingDiscounts || isApplyingDiscount}
                            className={`flex-1 rounded-xl bg-blue-600 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 ${isKiosk ? 'px-5 py-4 text-base' : 'px-4 py-2.5 text-sm'}`}
                        >
                            {appliedOfferCount > 0 ? 'Manage Offers' : 'View Offers'}
                        </button>
                        {appliedOfferCount > 0 && (
                            <button
                                type="button"
                                onClick={onRemoveAllDiscounts}
                                disabled={isApplyingDiscount}
                                className={`rounded-xl border border-slate-200 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 ${isKiosk ? 'px-5 py-4 text-base' : 'px-4 py-2.5 text-sm'}`}
                            >
                                {isGuestUser ? 'Clear' : 'Remove All'}
                            </button>
                        )}
                    </div>
                </div>

                {hasDiscount && (
                    <div className="pt-1">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key="applied"
                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                transition={{ duration: 0.25 }}
                                className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden"
                            >
                                <div className="flex items-center justify-between px-3.5 py-3">
                                    <div className="flex items-center gap-2.5">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                                            className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm flex-shrink-0"
                                        >
                                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </motion.div>
                                        <div>
                                            <p className="text-emerald-800 text-sm font-bold leading-tight">
                                                {appliedDiscountsList.length === 1 ? appliedDiscountsList[0].name : `${appliedDiscountsList.length} discounts applied`}
                                            </p>
                                            <p className="text-emerald-600 text-xs mt-0.5 font-semibold">
                                                Discount Applied: {money(discountAmt)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {appliedDiscountsList.length > 0 && (
                                    <div className="px-3.5 pb-3 flex flex-wrap gap-1.5">
                                        {appliedDiscountsList.map((discount) => (
                                            <span key={discount.primaryId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-full">
                                                {discount.code ? <span className="font-mono">{discount.code}</span> : discount.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="border-t border-emerald-200 px-3.5 py-2 bg-white/60">
                                    <span className="text-xs text-emerald-700 font-medium">
                                        {appliedDiscountsList.length} offer{appliedDiscountsList.length !== 1 ? 's' : ''} applied to this booking
                                    </span>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                )}

                <div className="border-t border-dashed border-slate-200 my-2" />

                <div className="space-y-3">
                    <div>
                        <p className="text-slate-800 font-bold text-sm">Select Payment Method</p>
                        <p className="text-slate-400 text-xs mt-0.5">Choose how you want to continue.</p>
                    </div>
                    <div className="space-y-2">
                        {availablePaymentOptions.map((option) => {
                            const isSelected = paymentMethod === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        onPaymentMethodChange(option.value);
                                        if (option.requiresReference && onOpenZelleModal) {
                                            onOpenZelleModal();
                                        }
                                    }}
                                    className={`w-full rounded-xl border text-left transition-all ${isKiosk ? 'px-5 py-5' : 'px-4 py-3'} ${isSelected
                                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`flex shrink-0 items-center justify-center rounded-xl border ${isKiosk ? 'h-14 w-14' : 'h-10 w-10'} ${isSelected ? 'border-blue-200 bg-white shadow-sm' : 'border-slate-200 bg-slate-50'}`}>
                                            {renderPaymentIcon(option.requiresReference ? 'zelle' : option.value, isSelected)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className={`font-semibold ${isKiosk ? 'text-lg' : 'text-sm'} ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>{option.title || option.label}</p>
                                            {option.description && (
                                                <p className={`mt-0.5 ${isKiosk ? 'text-sm' : 'text-xs'} ${isSelected ? 'text-blue-600' : 'text-slate-500'}`}>{option.description}</p>
                                            )}
                                        </div>
                                        <div className={`mt-0.5 rounded-full border flex items-center justify-center ${isKiosk ? 'h-7 w-7' : 'h-5 w-5'} ${isSelected ? 'border-blue-600 bg-white' : 'border-slate-300 bg-white'}`}>
                                            <div className={`rounded-full ${isKiosk ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'} ${isSelected ? 'bg-blue-600' : 'bg-transparent'}`} />
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                        {availablePaymentOptions.length === 0 && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                No online payment options are available for this booking.
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between px-1 py-1">
                    <div>
                        <p className="text-slate-800 font-bold text-sm">Total Due</p>
                        <AnimatePresence>
                            {hasDiscount && (
                                <motion.p
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="text-emerald-600 text-xs font-semibold mt-0.5"
                                >
                                    Discount Applied: {money(discountAmt)}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="text-right">
                        <AnimatePresence>
                            {hasDiscount && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="text-slate-400 text-sm line-through"
                                >
                                    {money(subtotal)}
                                </motion.p>
                            )}
                        </AnimatePresence>
                        <motion.span
                            key={finalAmt}
                            initial={{ scale: 0.85, opacity: 0.5 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            className={`font-extrabold text-3xl ${hasDiscount ? 'text-emerald-700' : 'text-slate-900'}`}
                        >
                            {money(finalAmt)}
                        </motion.span>
                    </div>
                </div>

                {remainingTime !== 0 && remainingTime?.minutes < 5 && (
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2"
                    >
                        <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="text-sm font-bold text-amber-800">Session ending soon!</p>
                            <p className="text-xs text-amber-700 mt-0.5">Complete in {remainingTime.minutes}:{remainingTime.seconds.toString().padStart(2, '0')}</p>
                        </div>
                    </motion.div>
                )}

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { triggerHaptic(); onCheckout?.(); }}
                    disabled={loading || isProcessingPayment || !paymentMethod || !selectedPaymentOption || remainingTime === 0}
                    className={`w-full mt-2 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none ${isKiosk ? 'min-h-[64px] py-5 text-lg' : 'py-4 text-base'}`}
                >
                    {isProcessingPayment ? (
                        <><Spinner /> Processing</>
                    ) : (
                        <>
                            {renderPaymentIcon(selectedPaymentOption?.requiresReference ? 'zelle' : (selectedPaymentOption?.value || paymentMethod), true, 'button')}
                            {ctaLabel}
                        </>
                    )}
                </motion.button>

                <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Secure payment | SSL encrypted
                </p>
            </div>
        </div>
    );
};

export default PaymentSummaryCard;
